import { useCallback, useEffect, useRef, useState } from 'react';
import { EventBus } from '@campus/core';
import { useIsTouchDevice } from '../hooks/useIsMobile.js';
import { Prompt } from '../ds/index.js';

/** Écart (px) entre le point visé et le bas de l'invite. */
const PROMPT_GAP_PX = 8;

type Position = { x: number; y: number };

const LABELS = { mount: 'Prendre la trottinette', dismount: 'Descendre', sit: "S'asseoir", stand: 'Se lever', board: 'Ouvrir le tableau', notice: 'Lire le tableau' } as const;

/**
 * Trottinette : invite centrée au-dessus du point visé. Assise : invite à droite
 * de l'avatar, son bord gauche sur le point visé (bord droit de l'avatar) et
 * centrée en hauteur : l'écart est en pixels d'écran, l'invite ne retombe pas
 * sur l'avatar quand on dézoome.
 */
function placeAt(el: HTMLElement, pos: Position, beside: boolean): void {
  el.style.transform = beside
    ? `translate3d(${pos.x + PROMPT_GAP_PX}px, calc(${pos.y}px - 50%), 0)`
    : `translate3d(calc(${pos.x}px - 50%), calc(${pos.y}px - 100% - ${PROMPT_GAP_PX}px), 0)`;
}

/**
 * ScooterPrompt — « Prendre la trottinette » au-dessus de la trottinette libre
 * à portée, « Descendre » au-dessus de l'avatar quand il roule (ZoneScene.
 * checkScooters()) ; de même « S'asseoir » au-dessus d'un siège libre à portée,
 * « Se lever » quand on est assis. Même fonctionnement que DoorPrompt : position appliquée
 * directement sur le DOM à chaque frame, invite touchable sur tactile.
 */
export function ScooterPrompt() {
  const [mode, setMode] = useState<'mount' | 'dismount' | 'sit' | 'stand' | 'board' | 'notice' | null>(null);
  const isTouch = useIsTouchDevice();
  const elRef = useRef<HTMLElement | null>(null);
  const posRef = useRef<Position | null>(null);
  const besideRef = useRef(false);
  besideRef.current = mode === 'sit' || mode === 'stand';

  useEffect(() => {
    const onPrompt = ({ mode: m }: { mode: 'mount' | 'dismount' | 'sit' | 'stand' | 'board' | 'notice' }) => setMode(m);
    const onHidden = () => setMode(null);
    const onPosition = (pos: Position) => {
      posRef.current = pos;
      if (elRef.current) placeAt(elRef.current, pos, besideRef.current);
    };
    EventBus.on('scooter:prompt', onPrompt);
    EventBus.on('scooter:prompt-hidden', onHidden);
    EventBus.on('scooter:prompt-position', onPosition);
    return () => {
      EventBus.off('scooter:prompt', onPrompt);
      EventBus.off('scooter:prompt-hidden', onHidden);
      EventBus.off('scooter:prompt-position', onPosition);
    };
  }, []);

  const setEl = useCallback((el: HTMLElement | null) => {
    elRef.current = el;
    if (el && posRef.current) placeAt(el, posRef.current, besideRef.current);
  }, []);

  if (!mode) return null;
  const label = LABELS[mode];

  if (isTouch) {
    return (
      <button
        ref={setEl}
        type="button"
        aria-label={label}
        onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); EventBus.emit('input:action-press', {}); }}
        onClick={() => EventBus.emit('input:action-press', {})}
        className="pl-door-prompt-touch"
        style={{ position: 'absolute', left: 0, top: 0, whiteSpace: 'nowrap', pointerEvents: 'auto', zIndex: 40 }}
      >
        <Prompt>{label}</Prompt>
      </button>
    );
  }

  return (
    <div
      ref={setEl}
      style={{ position: 'absolute', left: 0, top: 0, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 40 }}
    >
      <Prompt keyHint="Espace">{label}</Prompt>
    </div>
  );
}
